import { useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { closeAllModals, modals } from '@mantine/modals';
import {
  Paper,
  Card,
  Box,
  Title,
  Group,
  Text,
  Button,
  SimpleGrid,
  Input,
  Alert,
  LoadingOverlay,
  Divider,
} from '@mantine/core';
import {
  IconEdit,
  IconInfoCircle,
  IconStackPop,
  IconArrowRight,
} from '@tabler/icons-react';

import usePartner from '../hooks/partners/usePartner.ts';
import useRefuelingContract from '../hooks/refueling-contracts/use-refueling-contract.ts';
import useDownloadRefuelingContractAttachment from '../hooks/refueling-contracts/use-download-refueling-contract-attachment.ts';
import useUpdateRefuelingContract from '../hooks/refueling-contracts/use-update-refueling-contract.ts';
import usePublishRefuelingContract from '../hooks/refueling-contracts/use-publish-refueling-contract.ts';
import { formatDate } from '../utils/DateUtil.ts';

import type { Attachment } from '../models/attachment.ts';
import type {
  MutateRefuelingContractFormValues,
  MutateRefuelingContractFormInitialValues,
} from '../components/ui/MutateRefuelingContractForm.tsx';
import type {
  DownloadRefuelingContractAttachmentRequest,
  UpdateRefuelingContractRequest,
  UploadRefuelingContractAttachmentRequest,
  DeleteRefuelingContractAttachmentRequest,
  PublishRefuelingContractRequest,
} from '../api/requests/refueling-contract-request.ts';
import { executeDownloadAttachment } from '../utils/attachment-utils.ts';

import MutateRefuelingContractForm from '../components/ui/MutateRefuelingContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import {
  RECEIPT_MODE_LABEL,
  REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL,
} from '../const/labels.ts';
import { notifySuccess } from '../utils/NotifySuccess.tsx';

export default function RefuelingContractEditPage() {
  // hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId: partnerId, id: id } = useParams();

  const queryClient = useQueryClient();
  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { refuelingContract, isLoading: isContractLoading } =
    useRefuelingContract(partnerId, id);
  const {
    mutate: downloadRefuelingContractAttachment,
    isPending: isDownloadRefuelingContractAttachmentPending,
  } = useDownloadRefuelingContractAttachment();
  const { mutate: updateContract, isPending: isUpdateContractPending } =
    useUpdateRefuelingContract();
  const { mutate: publishContract } = usePublishRefuelingContract();

  const [isCreatedNoticeClosed, setCreatedNoticeClosed] = useState(false);
  const [isAttachmentWarningClosed, setAttachmentWarningClosed] =
    useState(false);
  const [isEditMode, setEditMode] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isPartnerLoading || isContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // 不是 DRAFT 階段的合約都不可編輯
  if (refuelingContract && refuelingContract.state !== 'DRAFT') {
    navigate(`/partners/${partnerId}/refueling-contracts/${id}`);
  }

  // constants
  const lastPagePath: string = `/partners/${partnerId}`;

  const isAttachmentsValid: boolean =
    refuelingContract.attachments.length === 1;
  const shouldDisplayCreatedNotice: boolean =
    !!state?.created && !isCreatedNoticeClosed;
  const shouldDisplayAttachmentWarning: boolean =
    !isAttachmentsValid && !isAttachmentWarningClosed;
  const isPublishable: boolean = !isEditMode && isAttachmentsValid;

  const initialFormValues: MutateRefuelingContractFormInitialValues = {
    name: refuelingContract.name,
    startAt: refuelingContract.startAt,
    endAt: refuelingContract.endAt,
    disbursementFeeProcessingMethod:
      refuelingContract.disbursementFeeProcessingMethod,
    gasStationNotificationEndpoint:
      refuelingContract.gasStationNotificationEndpoint,
    gasBaseDiscount: refuelingContract.gasBaseDiscount,
    dieselBaseDiscount: refuelingContract.dieselBaseDiscount,
    receiptMode: refuelingContract.receiptMode,
    gasStationAccountOwnerType: refuelingContract.gasStationAccountOwnerType,
    gasStationAccountThreshold: refuelingContract.gasStationAccountThreshold,
    gasStationAccountAutoDepositAmount:
      refuelingContract.gasStationAccountAutoDepositAmount,
    existingAttachments: refuelingContract.attachments,
  };

  // callback functions
  function onDownloadAttachment(attachment: Attachment): void {
    if (isDownloadRefuelingContractAttachmentPending) return;

    const request: DownloadRefuelingContractAttachmentRequest = {
      partnerId: partnerId,
      contractId: refuelingContract.id,
      attachmentId: attachment.id,
    };

    downloadRefuelingContractAttachment(request, {
      onSuccess: (downloadUrl) => {
        executeDownloadAttachment(downloadUrl, attachment.displayName);
        notifySuccess('下載檔案成功');
      },
    });
  }
  function onSubmit(values: MutateRefuelingContractFormValues): void {
    if (isUpdateContractPending) return;

    const updateRequest: UpdateRefuelingContractRequest = {
      partnerId: partnerId,
      id: refuelingContract.id,
      name: values.name,
      startAt: values.startAt,
      endAt: values.endAt,
      disbursementFeeProcessingMethod: values.disbursementFeeProcessingMethod,
      gasStationNotificationEndpoint: values.gasStationNotificationEndpoint,
      gasBaseDiscount: values.gasBaseDiscount,
      dieselBaseDiscount: values.dieselBaseDiscount,
      receiptMode: values.receiptMode,
      gasStationAccountOwnerType: values.gasStationAccountOwnerType,
      gasStationAccountThreshold:
        values.gasStationAccountOwnerType === 'JustLog'
          ? null
          : values.gasStationAccountThreshold,
      gasStationAccountAutoDepositAmount:
        values.gasStationAccountOwnerType === 'JustLog'
          ? null
          : values.gasStationAccountAutoDepositAmount,
    };

    const uploadAttachmentRequest: UploadRefuelingContractAttachmentRequest =
      values.newAttachments.map((attachment) => {
        return {
          partnerId: partnerId,
          contractId: refuelingContract.id,
          file: attachment.file,
        };
      })[0];

    const deleteAttachmentRequest: DeleteRefuelingContractAttachmentRequest =
      values.deletedAttachmentIds.map((id) => {
        return {
          partnerId: partnerId,
          contractId: refuelingContract.id,
          attachmentId: id,
        };
      })[0];

    updateContract(
      [updateRequest, uploadAttachmentRequest, deleteAttachmentRequest],
      {
        onSuccess: () => {
          notifySuccess('更新加油站合約成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'refuelingContract',
              refuelingContract.id,
            ],
          });
          setAttachmentWarningClosed(false);
          setEditMode(false);
        },
      },
    );
  }
  function onPublish(): void {
    const request: PublishRefuelingContractRequest = {
      partnerId: partnerId,
      id: refuelingContract.id,
    };

    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: <Text fw={700}>發佈合約</Text>,
      closeOnEscape: true,
      closeOnClickOutside: true,
      centered: true,
      innerProps: {
        modalBody: `是否確認發佈此合約？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });

          publishContract(request, {
            onSuccess: () => {
              closeAllModals();
              // 轉導至 detailPage
              navigate(`/partners/${partnerId}/refueling-contracts/${id}`, {
                state: { published: true },
              });
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  }

  return (
    <>
      <Box mb={70}>
        <PreviousPageButton href={lastPagePath} content={'返回業者詳情頁'} />

        {shouldDisplayCreatedNotice && (
          <Alert
            variant="light"
            color="green"
            mb={20}
            icon={<IconInfoCircle />}
            withCloseButton
            onClose={() => setCreatedNoticeClosed(true)}
            title="成功新增合約"
          />
        )}
        {shouldDisplayAttachmentWarning && (
          <Alert
            variant="light"
            color="yellow"
            mb={20}
            icon={<IconInfoCircle />}
            withCloseButton
            onClose={() => setAttachmentWarningClosed(true)}
            title="尚未上傳合約附件或已存在多份合約附件"
          />
        )}

        <Paper radius="lg" p="xl" shadow="xs">
          {isEditMode ? (
            <>
              <Group justify="space-between">
                <Title order={3}>{`${partner.name} | 加油站合約`}</Title>
                <Group>
                  <Button
                    variant="outline"
                    onClick={() => setEditMode(false)}
                    fw={400}
                  >
                    取消
                  </Button>
                  <Button
                    type="submit"
                    form="mutateRefuelingContractForm"
                    loading={isUpdateContractPending}
                    fw={400}
                  >
                    儲存
                  </Button>
                </Group>
              </Group>

              <MutateRefuelingContractForm
                initialValues={initialFormValues}
                onSubmit={onSubmit}
              />
            </>
          ) : (
            <>
              <Group justify="space-between">
                <Title order={3}>{`${partner.name} | 加油站合約`}</Title>
                <Button
                  leftSection={<IconEdit size={20} />}
                  variant="outline"
                  onClick={() => setEditMode(true)}
                  fw={400}
                >
                  編輯
                </Button>
              </Group>

              <Text size="sm" fw={500} c="gray" mt={40}>
                基本設定
              </Text>
              <Divider mt={10} mb={20} />
              <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                <Input.Wrapper withAsterisk label="合約名稱">
                  <Text>{refuelingContract.name}</Text>
                </Input.Wrapper>
                <Input.Wrapper withAsterisk label="合約生效起迄日">
                  <Group>
                    <Text>{`${formatDate(refuelingContract.startAt)}`}</Text>
                    <IconArrowRight size={20} />
                    <Text>{`${formatDate(refuelingContract.endAt)}`}</Text>
                  </Group>
                </Input.Wrapper>
                <Input.Wrapper label="手續費扣除方式">
                  <Text>
                    {
                      REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL[
                        refuelingContract.disbursementFeeProcessingMethod
                      ]
                    }
                  </Text>
                </Input.Wrapper>
                <Input.Wrapper label="加油站交易通知網址">
                  <Text>
                    {!refuelingContract.gasStationNotificationEndpoint
                      ? '-'
                      : refuelingContract.gasStationNotificationEndpoint}
                  </Text>
                </Input.Wrapper>
                <Input.Wrapper label="發票開立方式">
                  <Text>
                    {RECEIPT_MODE_LABEL[refuelingContract.receiptMode]}
                  </Text>
                </Input.Wrapper>
              </SimpleGrid>

              <Text size="sm" fw={500} c="gray" mt={40}>
                分潤設定
              </Text>
              <Divider mt={10} mb={20} />
              <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                <Input.Wrapper withAsterisk label="汽油分潤條件">
                  <Text>{`${refuelingContract.gasBaseDiscount}  公升/元`}</Text>
                </Input.Wrapper>
                <Input.Wrapper withAsterisk label="柴油分潤條件">
                  <Text>{`${refuelingContract.dieselBaseDiscount} 公升/元`}</Text>
                </Input.Wrapper>
              </SimpleGrid>

              <Text size="sm" fw={500} c="gray" mt={60}>
                現銷金設定
              </Text>
              <Divider mt={10} mb={20} />
              <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                <Input.Wrapper label="現銷金帳戶類型">
                  <Text>
                    {
                      GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL[
                        refuelingContract.gasStationAccountOwnerType
                      ]
                    }
                  </Text>
                </Input.Wrapper>
                {refuelingContract.gasStationAccountOwnerType !== 'JustLog' && (
                  <>
                    <Input.Wrapper label="自動補款門檻">
                      <Text>
                        {refuelingContract.gasStationAccountThreshold}
                      </Text>
                    </Input.Wrapper>
                    <Input.Wrapper label="自動補款金額">
                      <Text>
                        {refuelingContract.gasStationAccountAutoDepositAmount}
                      </Text>
                    </Input.Wrapper>
                  </>
                )}
              </SimpleGrid>

              <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
                合約上傳
              </Text>
              <Card
                shadow="none"
                style={{ border: '2px dashed var(--mantine-color-gray-2 )' }}
              >
                <Text size="sm" fw={500} inline mb={10}>
                  已選擇檔案：
                </Text>
                {refuelingContract.attachments.map((attachment) => {
                  return (
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        {attachment.displayName}
                      </Text>
                      <Button
                        variant="transparent"
                        onClick={() => onDownloadAttachment(attachment)}
                      >
                        下載檔案
                      </Button>
                    </Group>
                  );
                })}
              </Card>
            </>
          )}
        </Paper>
      </Box>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            onClick={onPublish}
            loading={false}
            disabled={!isPublishable}
            fw={400}
            leftSection={<IconStackPop />}
          >
            發佈
          </Button>
        </Group>
      </Box>
    </>
  );
}
